import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Package, Truck, MapPin, Check, Clock, AlertTriangle } from 'lucide-react';

export const C = {
  yellow: '#FFCC00',
  red: '#D40511',
  ink: '#111111',
  paper: '#FAFAF7',
};

export const CourierShell = ({ children }) => {
  const loc = useLocation();
  const nav = useNavigate();
  const links = [
    { to: '/courier/quote', label: 'Get a Quote' },
    { to: '/courier/ship', label: 'Ship Now' },
    { to: '/courier/track', label: 'Track' },
    { to: '/courier/shipments', label: 'My Shipments' },
  ];
  return (
    <div className="min-h-screen" style={{ background: C.paper }}>
      <header className="sticky top-0 z-40" style={{ background: C.yellow }}>
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/courier" className="flex items-center gap-2" data-testid="courier-logo">
            <Package className="h-6 w-6" style={{ color: C.red }} strokeWidth={2.5} />
            <span className="font-black tracking-tight text-xl" style={{ color: C.ink }}>
              TEKSTAR<span style={{ color: C.red }}>COURIER</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`courier-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-2 text-sm font-bold rounded-md transition-colors ${
                  loc.pathname === l.to ? 'bg-black text-white' : 'text-black hover:bg-black/10'
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <button onClick={() => nav('/')} className="text-xs font-bold text-black/70 hover:text-black" data-testid="courier-exit">
            ← Tekstar
          </button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8">{children}</main>
      <footer className="border-t border-black/10 mt-12 py-6 text-center text-xs text-black/50">
        Tekstar Courier · Zambia & International · Powered by Tekstar
      </footer>
    </div>
  );
};

const STAGE_META = {
  received: { label: 'Received', icon: Package },
  shipped: { label: 'Shipped', icon: Truck },
  out_for_delivery: { label: 'Out for Delivery', icon: MapPin },
  delivered: { label: 'Delivered', icon: Check },
};

export const CourierStepper = ({ stages, stageIndex, status }) => {
  const isException = status === 'exception' || status === 'returned';
  return (
    <div className="w-full" data-testid="courier-stepper">
      <div className="flex items-center">
        {stages.map((s, i) => {
          const meta = STAGE_META[s] || { label: s, icon: Clock };
          const done = i <= stageIndex && !isException;
          const current = i === stageIndex && !isException;
          const Icon = meta.icon;
          return (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center" data-testid={`stepper-${s}`}>
                <div
                  className={`h-11 w-11 rounded-full flex items-center justify-center border-2 transition-all ${
                    done ? 'text-black' : 'bg-white text-black/30 border-black/15'
                  }`}
                  style={done ? { background: C.yellow, borderColor: C.ink } : {}}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <span className={`mt-2 text-[11px] font-bold text-center max-w-[80px] ${current ? 'text-black' : 'text-black/50'}`}>
                  {meta.label}
                </span>
              </div>
              {i < stages.length - 1 && (
                <div className="flex-1 h-1 mx-1 rounded-full" style={{ background: i < stageIndex && !isException ? C.yellow : 'rgba(0,0,0,0.1)' }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {isException && (
        <div className="mt-4 flex items-center gap-2 text-sm font-bold p-3 rounded-lg" style={{ background: '#FDE8E8', color: C.red }}>
          <AlertTriangle className="h-4 w-4" /> Delivery {status} — please contact support.
        </div>
      )}
    </div>
  );
};

export const QuoteCard = ({ quote, quoteRef, qr, onShip }) => {
  const b = quote.breakdown || {};
  const rows = [
    ['Base', b.base], ['Weight', b.weight], ['Zone uplift', b.zoneUplift],
    ['Handling/Customs', b.handling], ['Insurance', b.insurance], ['Processing fee', b.platformFee],
  ].filter(([, v]) => v && v > 0);
  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-black/5" data-testid="quote-card">
      <div className="px-6 py-4 flex items-center justify-between" style={{ background: C.ink }}>
        <div>
          <p className="text-[11px] uppercase tracking-widest text-white/50">Quote</p>
          <p className="font-mono font-bold text-white" data-testid="quote-ref">{quoteRef}</p>
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-black" style={{ background: C.yellow, color: C.ink }}>
          {quote.serviceName}
        </span>
      </div>
      <div className="p-6 grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-sm space-y-1.5">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between text-black/70">
                <span>{k}</span><span className="font-mono">K{Number(v).toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t border-black/10 pt-2 mt-2 flex justify-between items-center">
              <span className="font-bold text-black">Total</span>
              <span className="font-black text-2xl" data-testid="quote-total">K{Number(quote.total).toFixed(2)}</span>
            </div>
          </div>
          <div className="mt-4 text-xs text-black/50 space-y-1">
            <p>Chargeable weight: <b>{quote.chargeableKg} kg</b> (vol {quote.volumetricKg} kg)</p>
            <p className="flex items-center gap-1"><Clock className="h-3 w-3" /> ETA {quote.eta}</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center border-l border-black/5 pl-6">
          {qr && <img src={qr} alt="Quote QR" className="h-32 w-32" data-testid="quote-qr" />}
          <p className="text-[10px] text-black/40 mt-2 text-center">Scan to retrieve this quote</p>
        </div>
      </div>
      {onShip && (
        <button onClick={onShip} data-testid="quote-ship-now-btn"
          className="w-full py-4 font-black text-black text-lg transition-transform hover:scale-[1.01]"
          style={{ background: C.yellow }}>
          Ship Now →
        </button>
      )}
    </div>
  );
};

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold text-black/60 uppercase tracking-wide">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export const inputCls =
  'w-full px-3 py-2.5 rounded-lg border border-black/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-black/80';
